'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Button,
  Switch,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Divider,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  InputAdornment,
  Paper,
  ButtonBase,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Trash2,
  Settings,
  LayoutDashboard,
  Gift,
  Database,
  Plug,
  Users,
  ChevronUp,
  ChevronDown,
  GripVertical,
} from 'lucide-react';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, TouchSensor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { getReferelInfo, updateReferelnfo } from '../api/users.api';
import {
  changeCategoryAvailability,
  changeServicesAvailability,
  getAppSetting,
  getCategoriesAvailability,
  getProcessors,
  getServicesAvailability,
  updateAppSetting,
} from '../api/services';

// Assuming these are external components or defined elsewhere in your project
// If they are not, you would need to define them here or import them correctly.
import SettingCard from './components/SettingCard';
import ModalC from './components/CModal';
import CurrentServiceCard from './components/CurrentServiceCard';

// CategoryControl remains the same.
const CategoryControl = ({
  id,
  name,
  status,
  processors,
  currentProcessor,
  changeAppCategoryAvailability,
  successRate,
  successRateAuto,
  indexValue,
  onMoveUp,
  onMoveDown,
  showUpButton,
  showDownButton,
}) => {
  const [change, setChange] = useState(false);
  const [categoryStatus, setCategoryStatus] = useState(status);
  const [processorSelected, setProcessorSelected] = useState(currentProcessor);
  const [rate, setRate] = useState(successRate);
  const [rateAuto, setRateAuto] = useState(successRateAuto);
  const [index, setIndex] = useState(indexValue || 0);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  useEffect(() => {
    setIndex(indexValue || 0);
  }, [indexValue]);

  // 70 > green, 50 > yellow, < 50 > red
  // eslint-disable-next-line no-nested-ternary
  const successRateColor = rate >= 70 ? 'green' : rate >= 50 ? 'yellow' : 'red';

  const handleCategoryChange = async (newStatus, newProcessor, newRate, newRateAuto, newIndex) => {
    const payload = {
      processors: newProcessor ?? processorSelected,
      status: newStatus ?? categoryStatus,
      successRate: newRate ?? rate,
      successRateAuto: newRateAuto ?? rateAuto,
      index: newIndex ?? index,
    };
    await changeAppCategoryAvailability(id, payload);
  };

  return (
    <Paper
      ref={setNodeRef}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
      }}
      elevation={0}
      sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'white' }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton size="small" sx={{ cursor: 'grab', color: 'grey.400', p: 0.5 }} {...attributes} {...listeners}>
            <GripVertical size={16} />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              borderRadius: '50%',
              width: 24,
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {indexValue ?? 0}
          </Box>
          <Typography variant="body1" fontWeight={500}>
            {name}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={onMoveUp} disabled={!showUpButton}>
            <ChevronUp size={18} />
          </IconButton>
          <IconButton size="small" onClick={onMoveDown} disabled={!showDownButton}>
            <ChevronDown size={18} />
          </IconButton>
          <ButtonBase onClick={() => setChange(!change)} sx={{ borderRadius: 1, px: 1.5, py: 0.5, ml: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {change ? 'Cancel' : 'Change'}
            </Typography>
          </ButtonBase>
        </Stack>
      </Stack>

      {change && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormGroup>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Status:</Typography>
                <Typography variant="body2" color={categoryStatus ? 'success.main' : 'error.main'}>
                  {categoryStatus ? 'ON' : 'OFF'}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={categoryStatus}
                      onChange={() => {
                        const newStatus = !categoryStatus;
                        setCategoryStatus(newStatus);
                        handleCategoryChange(newStatus, processorSelected, rate, rateAuto, index);
                      }}
                      name="status"
                    />
                  }
                  label=""
                  sx={{ ml: 0 }}
                />
              </Stack>
            </FormGroup>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Change Category Processor</InputLabel>
            <Select
              value={processorSelected}
              onChange={(e) => {
                const newProcessor = e.target.value;
                setProcessorSelected(newProcessor);
                handleCategoryChange(categoryStatus, newProcessor, rate, rateAuto, index);
              }}
            >
              {processors?.map((pr) => (
                <MenuItem key={pr.identifier} value={pr.identifier}>
                  {pr.name} - {pr.identifier}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={rateAuto}
                onChange={(e) => {
                  const newRateAuto = e.target.checked;
                  setRateAuto(newRateAuto);
                  handleCategoryChange(categoryStatus, processorSelected, rate, newRateAuto, index);
                }}
                name="rateAuto"
              />
            }
            label="Auto Success Rate"
          />
          <TextField
            label="Success Rate"
            value={rate}
            disabled={rateAuto}
            onBlur={() => handleCategoryChange(categoryStatus, processorSelected, rate, rateAuto, index)}
            onChange={(e) => {
              const newRate = e.target.value;
              setRate(newRate);
            }}
          />
          <TextField
            label="Sort Order Index"
            type="number"
            value={index}
            onBlur={() => handleCategoryChange(categoryStatus, processorSelected, rate, rateAuto, index)}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setIndex(Number.isNaN(val) ? '' : val);
            }}
          />
        </Stack>
      )}

      <Stack spacing={1} mt={2} alignItems="center">
        <Box
          sx={{
            width: '100%',
            height: '10px',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: '5px',
          }}
        >
          <Box sx={{ borderRadius: '5px', width: `${rate}%`, height: '100%', bgcolor: successRateColor }} />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Success Rate: {rate}%</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

const CURRENT_SERVICES = [
  { id: null, message: '', title: 'Airtime Service Checkout', name: 'airtime', service: 'Airtime', status: false },
  { id: null, message: '', title: 'Data Service Checkout', name: 'data', service: 'Data', status: false },
  {
    id: null,
    message: '',
    title: 'Electricity Service Checkout',
    name: 'electricity',
    service: 'Electricity',
    status: false,
  },
  { id: null, message: '', title: 'TV Service Checkout', name: 'cable', service: 'Cable', status: false },
  {
    id: null,
    message: '',
    title: 'Result Check Service Checkout',
    name: 'result_check',
    service: 'ResultCheck',
    status: false,
  },
];

const settingsCategories = [
  {
    id: 'maintenance-mode',
    title: 'Maintenance Mode',
    description: 'Control application availability during maintenance.',
    icon: <Settings size={24} />,
  },
  {
    id: 'service-management',
    title: 'Service Management',
    description: 'Enable or disable specific services and their categories.',
    icon: <LayoutDashboard size={24} />,
  },
  {
    id: 'promotions',
    title: 'Promotions',
    description: 'Manage promotional content and campaigns.',
    icon: <Gift size={24} />,
  },
  {
    id: 'data-manual-services',
    title: 'Data & Manual Services',
    description: 'Configure manual data limits and other manual services.',
    icon: <Database size={24} />,
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Set up and manage third-party service processors.',
    icon: <Plug size={24} />,
  },
  {
    id: 'referral-program',
    title: 'Referral Program',
    description: 'Define rules and rewards for your referral program.',
    icon: <Users size={24} />,
  },
];

const formatDateTimeLocal = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  } catch (error) {
    return '';
  }
};

export default function AppSystemSetting() {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('main'); // Controls which section is displayed

  // All state variables from the previous version
  const [state, setState] = useState({
    service: false,
    checkout: false,
  });
  const [message, setMessage] = useState('');
  const [manualDataLimit, setManualDataLimit] = useState(undefined);
  const [services, setServices] = useState(CURRENT_SERVICES);
  const [categories, setCategories] = useState([]);
  const [currentServices, setCurrentServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changeStatusLoading, setChangeStatusLoading] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [currentProcessors, setCurrentProcessors] = useState([]);
  const [processorHeaders, setProcessorHeaders] = useState({});
  const [referelInfo, setReferelInfo] = useState({});
  const [updateReferelInfoModal, setUpdateReferelInfoModal] = useState(false);
  const [referelUpdateLoading, setReferelUpdateLoading] = useState(false);
  const [dataLimitUpdateLoading, setDataLimitUpdateLoading] = useState(false);
  const [appSetting, setAppSetting] = useState(null);
  const [updateManualDataLimitModal, setUpdateManualDataLimitModal] = useState(false);
  const [serviceProcessorSetting, setServiceProcessorSetting] = useState(false);
  const [processorSettings, setProcessorSettings] = useState([]);
  const [nextId, setNextId] = useState(uuidv4());
  const [referralSettings, setReferralSettings] = useState({
    enabled: false,
    trigger: 'first_deposit',
    type: 'percentage',
    value: 10,
    maxReward: 1000,
    minWithdrawableAmount: 500,
    isServiceReferralEnabled: false,
    serviceReferralStartAt: '',
    depositReferralStartAt: '',
  });
  const [referralSettingsLoading, setReferralSettingsLoading] = useState(false);
  const [referralTab, setReferralTab] = useState(0);
  const [defaultCampaignRedirectUrl, setDefaultCampaignRedirectUrl] = useState('');
  const [androidPackageId, setAndroidPackageId] = useState('');
  const [iosAppId, setIosAppId] = useState('');
  const [webAppBaseUrl, setWebAppBaseUrl] = useState('');
  const [campaignRedirectUpdateLoading, setCampaignRedirectUpdateLoading] = useState(false);

  const selectedCategoryList = selectedService
    ? categories
        .filter((cat) => cat.service === selectedService.service)
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    : [];

  const handleReorderCategory = async (categoryId, direction) => {
    const currentList = [...selectedCategoryList];
    const indexInList = currentList.findIndex((cat) => cat.id === categoryId);
    if (indexInList === -1) return;

    const swapWithIndex = direction === 'up' ? indexInList - 1 : indexInList + 1;
    if (swapWithIndex < 0 || swapWithIndex >= currentList.length) return;

    // Build index updates
    const updatedIndices = currentList.map((cat, idx) => {
      if (idx === indexInList) {
        return { id: cat.id, index: swapWithIndex };
      }
      if (idx === swapWithIndex) {
        return { id: cat.id, index: indexInList };
      }
      return { id: cat.id, index: idx };
    });

    // Create a map of changes for O(1) lookup during state update
    const updatesMap = {};
    updatedIndices.forEach((item) => {
      updatesMap[item.id] = item.index;
    });

    // Save previous state for fallback/rollback in case of error
    const previousCategories = [...categories];

    // Optimistically update local categories state immediately for instant UI swap
    const optimisticallyUpdatedCategories = categories.map((cat) => {
      if (updatesMap[cat.id] !== undefined) {
        return { ...cat, index: updatesMap[cat.id] };
      }
      return cat;
    });

    setCategories(optimisticallyUpdatedCategories);

    // Perform API updates in the background without blocking the UI
    try {
      const promises = updatedIndices.map((update) => changeCategoryAvailability(update.id, { index: update.index }));
      await Promise.all(promises);
      // Silently sync the fresh database state in background
      const request = await getCategoriesAvailability();
      if (request.ok) {
        setCategories(request.data.data);
      }
    } catch (error) {
      // Revert change on backend failure
      setCategories(previousCategories);
      toast.error('Failed to reorder categories. Reverting order.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selectedCategoryList.findIndex((cat) => cat.id === active.id);
    const newIndex = selectedCategoryList.findIndex((cat) => cat.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedList = arrayMove(selectedCategoryList, oldIndex, newIndex);

    // Save previous state for fallback/rollback in case of error
    const previousCategories = [...categories];

    // Build the updated list of categories with re-indexed values optimistically
    const optimisticallyUpdatedCategories = categories.map((cat) => {
      const matchIndex = reorderedList.findIndex((item) => item.id === cat.id);
      if (matchIndex === -1) return cat;
      return { ...cat, index: matchIndex };
    });

    setCategories(optimisticallyUpdatedCategories);

    const updatedIndices = reorderedList.map((cat, idx) => ({
      id: cat.id,
      index: idx,
    }));

    try {
      const promises = updatedIndices.map((update) => changeCategoryAvailability(update.id, { index: update.index }));
      await Promise.all(promises);
      const request = await getCategoriesAvailability();
      if (request.ok) {
        setCategories(request.data.data);
      }
    } catch (error) {
      setCategories(previousCategories);
      toast.error('Failed to reorder categories. Reverting order.');
    }
  };

  // All handlers from the previous version
  const toggleUpdateReferelInfo = () => setUpdateReferelInfoModal(!updateReferelInfoModal);
  const toggleUpdateManualDataLimit = () => setUpdateManualDataLimitModal(!updateManualDataLimitModal);

  const handleChangeReferralSettings = (e) => {
    const { name, value, type, checked } = e.target;
    setReferralSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' || name === 'enabled' || name === 'isServiceReferralEnabled' ? checked : value,
    }));
  };

  const handleSaveReferralSettings = async () => {
    setReferralSettingsLoading(true);
    const request = await updateAppSetting({ referralSettings });
    if (request.ok) {
      toast.success('Referral settings updated successfully!');
      setReferralSettingsLoading(false);
      await handleGetAppSetting();
    } else {
      toast.error(request.data?.message || 'Failed to update referral settings.');
    }
    setReferralSettingsLoading(false);
  };

  const handleChangeReferalInfo = (e) => {
    if (e.target.name === 'status') {
      setReferelInfo({ ...referelInfo, [e.target.name]: e.target.checked });
      return;
    }
    setReferelInfo({ ...referelInfo, [e.target.name]: e.target.value });
  };

  const handleChange = (event) => {
    setState({
      ...state,
      [event.target.name]: event.target.checked,
    });
  };

  const handleChangeServiceStatus = (service) => {
    setState({ ...state, checkout: service.status });
    setMessage(service.message);
    const category = categories.filter((cat) => cat.service === service.service);
    setSelectedCategory(category);
    setSelectedService(service);
  };

  const getAppServiceAvailability = async () => {
    setLoading(true);
    const request = await getServicesAvailability();
    if (request.ok) {
      const { data } = request.data;
      const newService = services?.map((service) => {
        const origin = data.find((item) => item.name === service.name);
        return { ...service, status: origin.status, name: origin.name, id: origin.id, message: origin.message };
      });
      setServices(newService);
      setCurrentServices(data);
    }
    setLoading(false);
  };

  const getAppCategoryAvailability = async () => {
    const request = await getCategoriesAvailability();
    if (request.ok) {
      const { data } = request.data;
      setCategories(data);
    }
  };

  const handleGetAppSetting = async () => {
    const request = await getAppSetting();
    if (request.ok) {
      const { data } = request.data;
      const transformedProcessors = data?.service_processor?.current_processors?.map((p) => {
        const headerMatch = Object.values(processorHeaders).find((header) => p.apiKey.startsWith(`${header} `));
        if (headerMatch) {
          return {
            ...p,
            identifier: p.identifier || p.name,
            headerType: headerMatch,
            apiKey: p.apiKey.replace(`${headerMatch} `, ''),
          };
        }
        return { ...p, identifier: p.identifier || p.name, headerType: '' };
      });
      setProcessorSettings(transformedProcessors || []);
      setServiceProcessorSetting(data?.serviceProcessorSetting === 'env');
      setCurrentProcessors(data.service_processor?.current_processors || []);
      setAppSetting(data);
      setDefaultCampaignRedirectUrl(data?.defaultCampaignRedirectUrl || '');
      setAndroidPackageId(data?.androidPackageId || '');
      setIosAppId(data?.iosAppId || '');
      setWebAppBaseUrl(data?.webAppBaseUrl || '');
      if (data?.referralSettings) {
        setReferralSettings({
          enabled: false,
          trigger: 'first_deposit',
          type: 'percentage',
          value: 10,
          maxReward: 1000,
          minWithdrawableAmount: 500,
          isServiceReferralEnabled: false,
          serviceReferralStartAt: '',
          depositReferralStartAt: '',
          ...data.referralSettings,
        });
      }
    }
  };

  const handleUpdateAppSetting = async () => {
    setDataLimitUpdateLoading(true);
    const request = await updateAppSetting({ manualDataLimit });
    if (request.ok) {
      await handleGetAppSetting();
      toast.success('Data limit updated successfully');
      setDataLimitUpdateLoading(false);
      toggleUpdateManualDataLimit();
      setManualDataLimit(undefined);
      return;
    }
    setDataLimitUpdateLoading(false);
    toast.error(request.data?.message || 'Unable to update data limit');
  };

  const handleSaveCampaignRedirectUrl = async () => {
    if (!defaultCampaignRedirectUrl.trim()) {
      toast.warning('Default redirection URL cannot be empty');
      return;
    }
    setCampaignRedirectUpdateLoading(true);
    const request = await updateAppSetting({
      defaultCampaignRedirectUrl: defaultCampaignRedirectUrl.trim(),
      androidPackageId: androidPackageId.trim(),
      iosAppId: iosAppId.trim(),
      webAppBaseUrl: webAppBaseUrl.trim(),
    });
    if (request.ok) {
      await handleGetAppSetting();
      toast.success('App redirection targets updated successfully');
    } else {
      toast.error(request.data?.message || 'Update failed');
    }
    setCampaignRedirectUpdateLoading(false);
  };

  const changeAppServiceAvailability = async () => {
    setChangeStatusLoading(true);
    const payload = {
      message,
      status: state.checkout,
    };
    const request = await changeServicesAvailability(selectedService.id, payload);
    if (request.ok) {
      if (state.checkout !== selectedService.status) {
        // This function needs to be passed to CategoryControl
        // and updated to accept id and payload directly.
        // For now, assuming it's handled by the CategoryControl's internal state sync.
        // If not, you'd need to iterate through selectedCategory and call changeCategoryAvailability for each.
      }
      await getAppServiceAvailability();
      await getAppCategoryAvailability();
      setChangeStatusLoading(false);
      toast.success('Service Availability changed successfully');
      setSelectedService(null);
      return;
    }
    setChangeStatusLoading(false);
    toast.error(request.data ? request.data.message : 'Unable to complete your request');
  };

  // This function is now passed to CategoryControl
  const changeAppCategoryAvailability = async (id, payload) => {
    const request = await changeCategoryAvailability(id, payload);
    if (request.ok) {
      // Optionally update local state or re-fetch categories
      getAppCategoryAvailability();
      return request.data.data;
    }
    toast.error(request.data ? request.data.message : 'Unable to update category availability');
    return null;
  };

  const handleGetReferelInfo = async () => {
    const request = await getReferelInfo();
    if (request.ok) {
      setReferelInfo(request.data.data);
    }
  };

  const handleUpdateReferelInfo = async () => {
    setReferelUpdateLoading(true);
    const request = await updateReferelnfo(referelInfo);
    if (request.ok) {
      toggleUpdateReferelInfo();
      setReferelUpdateLoading(false);
      return;
    }
    toast.error(request.data ? request.data.message : 'Unable to complete your request. Please try again.');
    setReferelUpdateLoading(false);
  };

  const handleGetProcessors = useCallback(async () => {
    const response = await getProcessors({ withHeaders: true });
    if (response.ok) {
      setProcessors(response.data.data?.processors || []);
      setProcessorHeaders(response.data.data?.headers || {});
    }
  }, []);

  const handleAddProcessorSetting = () => {
    let notExistingProcessors = processors;
    if (processorSettings) {
      notExistingProcessors = processors.filter((processor) =>
        processorSettings.every((item) => item.name !== processor)
      );
    }
    const defaultProcessorName = notExistingProcessors.length > 0 ? notExistingProcessors[0] : '';
    const defaultHeaderType = processorHeaders[defaultProcessorName] || '';
    const newSetting = {
      id: nextId,
      active: true,
      apiKey: '',
      name: defaultProcessorName,
      identifier: defaultProcessorName,
      headerType: defaultHeaderType,
    };
    if (!processorSettings) {
      setProcessorSettings([newSetting]);
      setNextId(uuidv4());
      return;
    }
    setProcessorSettings([...processorSettings, newSetting]);
    setNextId(uuidv4());
  };

  const handleRemoveProcessorSetting = (id) => {
    setProcessorSettings(processorSettings.filter((setting) => setting.id !== id));
  };

  const handleChangeSetting = (id, key, value) => {
    setProcessorSettings(
      processorSettings?.map((setting) => {
        if (setting.id === id) {
          const updatedSetting = { ...setting, [key]: value };
          if (key === 'name') {
            updatedSetting.headerType = processorHeaders[value] || '';
            updatedSetting.identifier = value;
          }
          return updatedSetting;
        }
        return setting;
      })
    );
  };

  const handleSave = async () => {
    const invalidProcessorKey = processorSettings.find((processor) => processor.apiKey.length < 10);
    if (invalidProcessorKey) {
      toast.error('Invalid processor detected. Please ensure the API key is at least 10 characters long.');
      return;
    }

    const hasDuplicateKey = new Set(processorSettings.map((p) => p.identifier)).size !== processorSettings.length;

    if (hasDuplicateKey) {
      toast.error('Duplicate processor key identifier detected. Each processor must have a unique key identifier.');
      return;
    }

    const currentServiceProcessor = appSetting.service_processor || {};
    const payload = processorSettings.map((processor) => ({
      ...processor,
      identifier: processor?.identifier || processor?.name,
      token_key: `${processor?.identifier?.toUpperCase() || processor.name?.toUpperCase()}_TOKEN`,
      apiKey: processor.headerType ? `${processor.headerType} ${processor.apiKey}` : processor.apiKey,
    }));
    setDataLimitUpdateLoading(true);
    const request = await updateAppSetting({
      service_processor: { ...currentServiceProcessor, current_processors: payload },
      serviceProcessorSetting: serviceProcessorSetting ? 'env' : 'db',
    });
    if (request.ok) {
      await handleGetAppSetting();
      toast.success('Service processors updated successfully');
      setDataLimitUpdateLoading(false);
      return;
    }
    setDataLimitUpdateLoading(false);
    toast.error(request.data?.message || 'Service processor update failed');
  };

  // Initial data fetching
  useEffect(() => {
    handleGetProcessors();
  }, [handleGetProcessors]);

  useEffect(() => {
    if (Object.keys(processorHeaders).length > 0) {
      handleGetAppSetting();
    }
  }, [processorHeaders]);

  useEffect(() => {
    getAppServiceAvailability();
    getAppCategoryAvailability();
    handleGetReferelInfo();
  }, []);

  // Render logic based on currentView
  const renderSettingsContent = () => {
    const currentCategory = settingsCategories.find((cat) => cat.id === currentView);

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
          overflow: 'hidden',
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              {currentCategory.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {currentCategory.description}
            </Typography>
          </Box>

          {currentView === 'maintenance-mode' && (
            <SettingCard>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight="700">
                  Maintenance Mode
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  If you need to take your app offline, you can turn on Maintenance mode.
                </Typography>
                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={state.service}
                          onChange={(e) => setState({ ...state, service: e.target.checked })}
                          name="service"
                        />
                      }
                      label={`Maintenance mode is ${state.service ? 'ON' : 'OFF'}`}
                    />
                  </FormGroup>
                </FormControl>
              </Stack>
            </SettingCard>
          )}

          {currentView === 'service-management' && (
            <SettingCard>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight="700">
                  Service Checkout
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  If you don't want to stop the entire app, you can disable the checkout service or category.
                </Typography>
                <Paper
                  sx={{
                    padding: { xs: '10px', md: '20px' },
                    minHeight: 200,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: 'none',
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {services.map((service, index) => (
                        <CurrentServiceCard
                          key={index}
                          title={service.title}
                          status={service.status}
                          buttonTitle="Change"
                          onPress={() => handleChangeServiceStatus(service)}
                        />
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Stack>
            </SettingCard>
          )}

          {currentView === 'promotions' && (
            <Stack spacing={3}>
              <SettingCard>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight="700">
                    Promo Content Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Setup promo content such as for the Referral Program page.
                  </Typography>
                </Stack>
                <Paper
                  sx={{
                    padding: { xs: '10px', md: '20px' },
                    minHeight: 100,
                    mt: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: 'none',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={500}>
                      Referral Program Content
                    </Typography>
                    <Button onClick={toggleUpdateReferelInfo} variant="outlined" sx={{ borderRadius: 2 }}>
                      View
                    </Button>
                  </Stack>
                </Paper>
              </SettingCard>

              <SettingCard>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight="700">
                    Default Campaign Redirect URL
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure the fallback landing page or play store destination when users access downloads.
                  </Typography>
                </Stack>
                <Paper
                  sx={{
                    padding: { xs: '20px', md: '30px' },
                    mt: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: 'none',
                  }}
                >
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Campaign Base Redirection URL"
                      placeholder="https://yourdomain.com/download"
                      value={defaultCampaignRedirectUrl}
                      onChange={(e) => setDefaultCampaignRedirectUrl(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="Web App Base URL"
                      placeholder="e.g. https://app.yourdomain.com"
                      value={webAppBaseUrl}
                      onChange={(e) => setWebAppBaseUrl(e.target.value)}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Android Package ID"
                          placeholder="e.g. com.tenant.app"
                          value={androidPackageId}
                          onChange={(e) => setAndroidPackageId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="iOS App Store ID"
                          placeholder="e.g. id6739472659"
                          value={iosAppId}
                          onChange={(e) => setIosAppId(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end">
                      <LoadingButton
                        loading={campaignRedirectUpdateLoading}
                        onClick={handleSaveCampaignRedirectUrl}
                        variant="contained"
                        sx={{ borderRadius: 2 }}
                      >
                        Save Settings
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </Paper>
              </SettingCard>
            </Stack>
          )}

          {currentView === 'data-manual-services' && (
            <SettingCard>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight="700">
                  Manual Service
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Setup manual service settings such as updating the data limit.
                </Typography>
              </Stack>
              <Paper
                sx={{
                  padding: { xs: '10px', md: '20px' },
                  minHeight: 100,
                  mt: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: 'none',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body1" fontWeight={500}>
                    Update Data Limit
                  </Typography>
                  <Button onClick={toggleUpdateManualDataLimit} variant="outlined" sx={{ borderRadius: 2 }}>
                    View
                  </Button>
                </Stack>
              </Paper>
            </SettingCard>
          )}

          {currentView === 'integrations' && (
            <SettingCard>
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="700">
                  Service Processor Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add or Update the API key for each service processor.
                </Typography>

                <Box>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Processor Configurations
                  </Typography>
                  <Grid container spacing={2}>
                    {processorSettings?.map((setting) => (
                      <Grid item xs={12} sm={6} md={4} key={setting.id}>
                        <Paper
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 2,
                            p: 2,
                            bgcolor: 'white',
                          }}
                        >
                          <Stack direction="column" spacing={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel id={`processor-select-${setting.id}`}>Processor</InputLabel>
                              <Select
                                labelId={`processor-select-${setting.id}`}
                                value={setting.name}
                                label="Processor"
                                onChange={(e) => {
                                  handleChangeSetting(setting.id, 'name', e.target.value);
                                }}
                                sx={{ borderRadius: 2 }}
                              >
                                {processors.map((proc) => (
                                  <MenuItem key={proc} value={proc}>
                                    {proc}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              label="Processor Identifier"
                              fullWidth
                              value={setting.identifier}
                              defaultValue={setting.name}
                              onChange={(e) => handleChangeSetting(setting.id, 'identifier', e.target.value)}
                              placeholder="Enter Processor Identifier"
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                },
                              }}
                            />
                            <TextField
                              label="API Key"
                              fullWidth
                              value={setting.apiKey} // Display only the raw API key
                              onChange={(e) => handleChangeSetting(setting.id, 'apiKey', e.target.value)}
                              placeholder="Enter API Key"
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                                      {setting.headerType}
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={setting.active}
                                    onChange={(e) => handleChangeSetting(setting.id, 'active', e.target.checked)}
                                  />
                                }
                                label="Active"
                                sx={{ mr: 0 }}
                              />
                              <IconButton color="error" onClick={() => handleRemoveProcessorSetting(setting.id)}>
                                <Trash2 size={20} />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={handleAddProcessorSetting} sx={{ borderRadius: 2 }}>
                      Add Processor Setting
                    </Button>
                    <LoadingButton
                      loading={dataLimitUpdateLoading}
                      disabled={dataLimitUpdateLoading}
                      variant="contained"
                      onClick={handleSave}
                      sx={{ borderRadius: 2 }}
                    >
                      Save Settings
                    </LoadingButton>
                  </Stack>
                </Box>
              </Stack>
            </SettingCard>
          )}

          {currentView === 'referral-program' && (
            <SettingCard>
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="700">
                  Referral Program Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure the rules and rewards for your referral program.
                </Typography>

                <Tabs
                  value={referralTab}
                  onChange={(e, val) => setReferralTab(val)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2,
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                    },
                  }}
                >
                  <Tab label="Deposit Referral" />
                  <Tab label="Service Referral" />
                </Tabs>

                {referralTab === 0 && (
                  <Stack spacing={3}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={referralSettings.enabled}
                            onChange={handleChangeReferralSettings}
                            name="enabled"
                          />
                        }
                        label={`Deposit Referral is ${referralSettings.enabled ? 'ON' : 'OFF'}`}
                      />
                    </FormGroup>
                    {referralSettings.enabled && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Referral Starting Date"
                            name="depositReferralStartAt"
                            type="datetime-local"
                            value={formatDateTimeLocal(referralSettings.depositReferralStartAt)}
                            onChange={handleChangeReferralSettings}
                            InputLabelProps={{ shrink: true }}
                            helperText="Only users created on or after this date will qualify for deposit referrals."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Reward Trigger</InputLabel>
                            <Select
                              name="trigger"
                              value={referralSettings.trigger}
                              label="Reward Trigger"
                              onChange={handleChangeReferralSettings}
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="first_deposit">First Deposit</MenuItem>
                              <MenuItem value="all_deposits">All Deposits</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Reward Type</InputLabel>
                            <Select
                              name="type"
                              value={referralSettings.type}
                              label="Reward Type"
                              onChange={handleChangeReferralSettings}
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="percentage">Percentage</MenuItem>
                              <MenuItem value="constant">Constant</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Reward Value"
                            name="value"
                            type="number"
                            value={referralSettings.value}
                            onChange={handleChangeReferralSettings}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {referralSettings.type === 'percentage' ? '%' : 'NGN'}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Maximum Reward (Optional)"
                            name="maxReward"
                            type="number"
                            value={referralSettings.maxReward}
                            onChange={handleChangeReferralSettings}
                            helperText="Set to 0 for no limit. Applies only to percentage-based rewards."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Minimum Withdrawable Amount (Optional)"
                            name="minWithdrawableAmount"
                            type="number"
                            value={referralSettings.minWithdrawableAmount}
                            onChange={handleChangeReferralSettings}
                            helperText="Minimum referral earnings a user must have to withdraw. Set to 0 for no minimum."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Stack>
                )}

                {referralTab === 1 && (
                  <Stack spacing={3}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={referralSettings.isServiceReferralEnabled}
                            onChange={handleChangeReferralSettings}
                            name="isServiceReferralEnabled"
                          />
                        }
                        label={`Service Referral is ${referralSettings.isServiceReferralEnabled ? 'ON' : 'OFF'}`}
                      />
                    </FormGroup>
                    {referralSettings.isServiceReferralEnabled && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Referral Starting Date"
                            name="serviceReferralStartAt"
                            type="datetime-local"
                            value={formatDateTimeLocal(referralSettings.serviceReferralStartAt)}
                            onChange={handleChangeReferralSettings}
                            InputLabelProps={{ shrink: true }}
                            helperText="Only users created on or after this date will qualify for service referrals."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Stack>
                )}

                <Box sx={{ mt: 3 }}>
                  <LoadingButton
                    variant="contained"
                    loading={referralSettingsLoading}
                    onClick={handleSaveReferralSettings}
                    sx={{ borderRadius: 2 }}
                  >
                    Save Referral Settings
                  </LoadingButton>
                </Box>
              </Stack>
            </SettingCard>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: { xs: 2, md: 4 } }}>
          {currentView === 'main' ? (
            <IconButton
              onClick={() => navigate('/dashboard/settings')}
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setCurrentView('main')}
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {currentView === 'main'
              ? 'Application Settings'
              : settingsCategories.find((cat) => cat.id === currentView)?.title}
          </Typography>
        </Stack>

        {currentView === 'main' ? (
          <Grid container spacing={3}>
            {settingsCategories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Paper
                  component={ButtonBase}
                  onClick={() => setCurrentView(category.id)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    textAlign: 'left',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      borderColor: 'primary.main',
                      boxShadow: '0 6px 12px -2px rgb(0 0 0 / 0.15), 0 3px 6px -2px rgb(0 0 0 / 0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{category.icon}</Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                    {category.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          renderSettingsContent()
        )}

        {/* Modals (kept here as they are global to the settings section) */}
        <ModalC isOpen={selectedService} setOpen={() => setSelectedService(null)}>
          {selectedService && (
            <Stack spacing={3}>
              <Typography variant="h6" fontWeight={600}>
                Change status of {selectedService.name} service
              </Typography>
              <FormControl component="fieldset">
                <FormLabel component="legend">Service is currently {state.checkout ? 'ON' : 'OFF'}</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={<Switch checked={state.checkout} onChange={handleChange} name="checkout" />}
                    label={`${!state.checkout ? 'Enable all' : 'Disable All'}`}
                  />
                </FormGroup>
              </FormControl>
              {!state.checkout && (
                <TextField
                  label="Reason.."
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="State the reason for this change to be shown to your customers"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
              {selectedCategoryList && selectedCategoryList.length > 0 && state.checkout && (
                <Stack spacing={2}>
                  <Typography variant="body1" fontWeight={500}>
                    Categories Availability
                  </Typography>
                  <Divider />
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={selectedCategoryList.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack spacing={2}>
                        {selectedCategoryList.map((data, index) => (
                          <CategoryControl
                            key={data.id}
                            name={data.name}
                            id={data.id}
                            status={data.status}
                            successRate={data.successRate}
                            successRateAuto={data.successRateAuto}
                            currentProcessor={data.processor}
                            processors={appSetting?.service_processor?.current_processors || []}
                            changeAppCategoryAvailability={changeAppCategoryAvailability} // Pass the function
                            indexValue={data.index}
                            showUpButton={index > 0}
                            showDownButton={index < selectedCategoryList.length - 1}
                            onMoveUp={() => handleReorderCategory(data.id, 'up')}
                            onMoveDown={() => handleReorderCategory(data.id, 'down')}
                          />
                        ))}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                </Stack>
              )}
              <LoadingButton
                variant="contained"
                loading={changeStatusLoading}
                onClick={changeAppServiceAvailability}
                sx={{ borderRadius: 2 }}
              >
                Save
              </LoadingButton>
            </Stack>
          )}
        </ModalC>

        <ModalC isOpen={updateReferelInfoModal} setOpen={toggleUpdateReferelInfo}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Setup your Referral Program
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={referelInfo.status} onChange={handleChangeReferalInfo} name="status" />}
                label={`Referral Program is ${referelInfo.status ? 'ON' : 'OFF'}`}
              />
            </FormGroup>
            {referelInfo.status ? (
              <>
                <TextField
                  label="Referral Program title"
                  name="title"
                  value={referelInfo.title}
                  onChange={handleChangeReferalInfo}
                  placeholder="Give the referral program title"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  label="Referral Program descriptions"
                  multiline
                  rows={4}
                  name="subtitle"
                  value={referelInfo.subtitle}
                  onChange={handleChangeReferalInfo}
                  placeholder="Add referral program info"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </>
            ) : (
              <>
                <TextField
                  label="Referral Program end title"
                  name="statusTitle"
                  value={referelInfo.statusTitle}
                  onChange={handleChangeReferalInfo}
                  placeholder="Referral program end title"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  label="Referral Program end descriptions"
                  multiline
                  rows={4}
                  name="statusSubtitle"
                  value={referelInfo.statusSubtitle}
                  onChange={handleChangeReferalInfo}
                  placeholder="Referral program end descriptions"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </>
            )}
            <LoadingButton
              onClick={handleUpdateReferelInfo}
              loading={referelUpdateLoading}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Update
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={toggleUpdateReferelInfo}
              disabled={referelUpdateLoading}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
          </Stack>
        </ModalC>

        <ModalC isOpen={updateManualDataLimitModal} setOpen={toggleUpdateManualDataLimit}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Update Manual Data Limit
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Current Data Balance: {appSetting?.manualDataLimit}MB
            </Typography>
            <TextField
              fullWidth
              label="Manual Data Limit"
              type="number"
              value={manualDataLimit}
              onChange={(event) => {
                if (Number(event.target.value) || event.target.value === '') {
                  setManualDataLimit(event.target.value);
                }
              }}
              variant="outlined"
              sx={{
                my: '20px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <LoadingButton
              onClick={handleUpdateAppSetting}
              loading={dataLimitUpdateLoading}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Update
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={toggleUpdateManualDataLimit}
              disabled={dataLimitUpdateLoading}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
          </Stack>
        </ModalC>
      </Box>
    </Box>
  );
}
